import type { documentsDe } from './documents.de';
import type { Shape } from './en';

export const documentsEn: Shape<typeof documentsDe> = {
  placeholders: {
    companyName: 'Company',
    createdAt: 'Created on',
    author: 'Created by',
    authorRole: 'Job title of the author',
    page: 'Page number',
    pages: 'Number of pages',
  },
  placeholderGroups: { document: 'Document', page: 'Page' },
  blocks: {
    text: {
      label: 'Text',
      description: 'Free text with headings, tables, pictures and placeholders.',
    },
    pageBreak: { label: 'Page break', description: 'What follows starts on a new page.' },
    signature: {
      label: 'Signatures',
      description: 'Signature lines side by side, from four people on in further rows.',
    },
    findingsSummary: {
      label: 'Defect overview',
      description: 'Table with number, title, category, priority and status.',
    },
    findingsDetails: {
      label: 'Defects in detail',
      description:
        'Each defect with description, measure, legal basis, photos and plan or map detail.',
    },
    findingsTable: {
      label: 'Defect table',
      description: 'Defects as a table, grouped by category or group, in landscape if needed.',
    },
    findingsPlans: {
      label: 'Defect plans and map',
      description:
        'Each plan page with its markers on a page of its own, defects with a GPS point on a map.',
    },
    questionnaire: { label: 'Questionnaire', description: 'Questions and answers of the record.' },
    source: { label: 'Data of the record', description: 'Table or text from the record.' },
    attachments: {
      label: 'Attachments',
      description: 'PDF files of the record at the end, without header and footer.',
    },
  },
  templates: {
    title: 'Document templates: {{document}}',
    sync: 'Document templates',
    description: 'Layout of the document with cover, header, footer and content.',
    empty: 'No template for this document yet.',
    emptyDefaults: 'Take over the default content of the module or create a template of your own.',
    default: 'Default',
    inactive: 'Inactive',
    actions: 'Actions for “{{name}}”',
    makeDefault: 'Make default',
    activate: 'Activate',
    deactivate: 'Deactivate',
    copy: 'Copy',
    moveUp: 'Move “{{name}}” up',
    moveDown: 'Move “{{name}}” down',
    create: 'New template',
    createTitle: 'New template',
    copyTitle: 'Copy template',
    copyName: '{{name}} (copy)',
    importTitle: 'Import template',
    importDescription: 'The template is created with its pictures under this name.',
    name: 'Name',
    loadDefaults: 'Take over default content',
    defaultsLoaded_one: '{{count}} template taken over.',
    defaultsLoaded_other: '{{count}} templates taken over.',
    defaultsNone: 'All default templates exist already.',
    import: 'Import template',
    created: 'Template “{{name}}” created.',
    copied: 'Template “{{name}}” created.',
    imported: 'Template “{{name}}” imported.',
    deleted: 'Template “{{name}}” deleted.',
    saved: 'Template “{{name}}” saved.',
    deleteTitle: 'Delete template?',
    deleteDescription: '“{{name}}” is deleted with its pictures. Documents created from it remain.',
    errors: {
      'name-taken': 'A template with this name exists already.',
      'default-inactive': 'An inactive template cannot be the default.',
      'block-not-allowed': 'The template contains blocks this document does not offer.',
      'source-unknown': 'The template names data this document does not deliver.',
      'image-unknown': 'A picture of the template is missing. Upload it again.',
      'template-too-large': 'The template is too large.',
      'content-invalid': 'The content of the template is invalid.',
      importInvalid: 'The file contains no template that can be taken over.',
      importFailed: 'The file could not be read just now. Try again.',
      importImage: 'A picture of the template could not be taken over.',
      imageTooLarge: 'The picture is too large. PNG and JPEG up to 5 MB are allowed.',
      imageFailed: 'The picture could not be uploaded.',
    },
  },
  editor: {
    general: 'General',
    name: 'Name',
    orientation: 'Page format',
    portrait: 'Portrait',
    landscape: 'Landscape',
    cover: 'Cover',
    coverDescription: 'First page of the document, without header. Leave empty for no cover.',
    coverAlone: 'Cover alone on the first page',
    coverAloneDescription: 'The content then starts on the second page.',
    bands: 'Header and footer',
    bandsDescription:
      'On every page except the attachments, the header not on the cover. For a logo next to text insert a table with two columns; here it shows without lines.',
    header: 'Header',
    footer: 'Footer',
    blocks: 'Content',
    blocksDescription: 'Blocks in the order they appear in the document.',
    blocksEmpty: 'No content yet. Add the first block.',
    addBlock: 'Add block',
    removeBlock: 'Remove “{{name}}”',
    moveUp: 'Move “{{name}}” up',
    moveDown: 'Move “{{name}}” down',
    blockTitle: 'Heading',
    blockTitleDescription: 'Leave empty for no heading.',
    photos: 'Show photos',
    planDetail: 'Plan or map detail for each defect',
    groupBy: 'Grouping',
    groupByNone: 'None',
    groupByCategory: 'By category',
    groupByGroup: 'By group',
    location: 'Column “Location” with plan or map detail',
    responsible: 'Column “Responsible / deadline”',
    blockLandscape: 'In landscape',
    mapSections: 'Split long sites into sections',
    mapSectionsHint:
      'After the overview map, one map follows for about every kilometre. Off: one large map with all findings.',
    who: 'Signed by',
    whoAuthor: 'Person who creates the document',
    whoRecord: 'People of the record',
    note: 'Text above the signatures',
    placeAndDate: 'Show place and date',
    unsaved: 'Unsaved changes',
    discard: 'Discard',
  },
  richText: {
    toolbar: 'Formatting',
    undo: 'Undo',
    redo: 'Redo',
    blockType: 'Text style',
    paragraph: 'Paragraph',
    heading1: 'Heading 1',
    heading2: 'Heading 2',
    heading3: 'Heading 3',
    bold: 'Bold',
    italic: 'Italic',
    underline: 'Underline',
    bulletList: 'Bulleted list',
    numberedList: 'Numbered list',
    align: 'Alignment',
    alignLeft: 'Align left',
    alignCenter: 'Center',
    alignRight: 'Align right',
    alignJustify: 'Justify',
    insertTable: 'Insert table',
    table: 'Table',
    rowAbove: 'Insert row above',
    rowBelow: 'Insert row below',
    columnLeft: 'Insert column left',
    columnRight: 'Insert column right',
    headerRow: 'Header row',
    deleteRow: 'Delete row',
    deleteColumn: 'Delete column',
    deleteTable: 'Delete table',
    horizontalRule: 'Divider',
    image: 'Picture',
    imageSize: 'Picture size',
    imageSizeOriginal: 'Logo size',
    imageSizeSmall: 'A quarter',
    imageSizeMedium: 'Half width',
    imageSizeLarge: 'Three quarters',
    imageSizeFull: 'Full width',
    placeholder: 'Placeholder',
    placeholderSearch: 'Search placeholders',
    noResults: 'No results',
  },
  preview: {
    title: 'Preview',
    description: 'Built with sample data, the way the document will look.',
    refresh: 'Refresh preview',
    outdated: 'The preview does not show the latest changes yet.',
    loading: 'Building preview …',
    failed: 'The preview could not be built.',
    page: 'Page {{page}} of {{pages}}',
    download: 'Download as PDF',
  },
  create: {
    title: 'Create {{document}}',
    description: 'The document is built on this device, also without network.',
    template: 'Template',
    noTemplates: 'There is no active template for this document yet.',
    noTemplatesManage: 'Templates are set up in the settings of the module.',
    checking: 'Checking the data …',
    complete: 'Everything this template needs is there.',
    warnings: 'Please check',
    errors: 'Still missing',
    attach: 'Attach to the record',
    attachDescription: 'The PDF is stored with the files of the record.',
    attachOffline: 'Attaching needs a network. Download the document and attach it later.',
    attachQueued: 'The PDF waits on this device and is attached with the next sync.',
    submit: 'Create',
    created: 'The document was created.',
    attached: 'The document was created and attached to the record.',
    queued: 'The document was created and will be attached once the network is back.',
    attachFailed: 'The document was created but could not be attached.',
    attachTooLarge: 'The document was created but is too large to attach (at most {{size}}).',
    failed: 'The document could not be created.',
    loadFailed: 'The data for the document could not be loaded.',
    pdfErrors: {
      'attachment-missing': 'The attachment “{{name}}” is not available.',
      'attachment-encrypted':
        'The attachment “{{name}}” is protected by a password. Store a version without password.',
      'attachment-unreadable': 'The attachment “{{name}}” is damaged or not a PDF.',
    },
  },
  sample: {
    author: 'Jane Example',
    authorRole: 'Fire safety officer',
    signer1Name: 'John Sample',
    signer1Role: 'Property management',
    signer2Name: 'Clara Probe',
    signer2Role: 'Building services',
    place: 'Sampletown',
    categoryEscape: 'Escape routes',
    categoryDoors: 'Fire doors',
    categoryExtinguishers: 'Fire extinguishers',
    groupStructural: 'Structural fire protection',
    groupTechnical: 'Technical fire protection',
    statusOpen: 'Open',
    statusProgress: 'In progress',
    statusDone: 'Done',
    priorityHigh: 'High',
    priorityMedium: 'Medium',
    priorityLow: 'Low',
    planName: 'Floor plan',
    planFloor: 'GF',
    finding1Title: 'Escape route narrowed by stored goods',
    finding1Description:
      'Pallets stand in the aisle between the shelves. At its narrowest point the usable width of the escape route is only about 0.60 m.',
    finding1Measure:
      'Remove the pallets and keep the full width of the escape route clear at all times.',
    finding1LegalBasis: '§ 4 (4) ArbStättV; ASR A2.3, section 5',
    finding1Area: 'Hall 1',
    finding1Caption: 'Aisle between the shelves',
    finding2Title: 'Fire door held open with a wedge',
    finding2Description:
      'The door to the staircase is held open with a wooden wedge and cannot close in case of fire.',
    finding2Measure: 'Remove the wedge; if needed, have an approved hold-open system installed.',
    finding2LegalBasis: '§ 14 MBO',
    finding2Area: 'Staircase',
    finding2Caption: 'Door to the staircase',
    finding3Title: 'Fire extinguisher without valid inspection label',
    finding3Description:
      'The last inspection of the fire extinguisher was more than two years ago.',
    finding3Measure: 'Have the fire extinguisher inspected by a competent person.',
    finding3LegalBasis: 'ASR A2.2, section 6; DGUV Information 205-001',
    finding3Room: 'Office 0.04',
    finding4Title: 'Sign to the assembly point faded',
    finding4Measure: 'Replace the sign with a long-afterglow sign.',
    finding5Title: 'Fire brigade access clear and well signposted',
    finding5Description: 'The access is clear over its full width, the signs are easy to read.',
    responsible1: 'Example Logistics Ltd',
    responsible2: 'Sample Building Services',
    question1: 'Whole building inspected',
    question2: 'Last inspection of the fire alarm system',
    question3: 'Number of employees',
    question4: 'Particulars',
    answer4: 'Conversion on the upper floor, area closed off.',
    sourceText: 'This is where the text the record delivers appears.',
    column: 'Column {{n}}',
    cell: 'Sample',
    attachmentTitle: 'Attached document (sample)',
    attachmentName: 'Attachment.pdf',
  },
  pdf: {
    yes: 'Yes',
    no: 'No',
    question: 'Item',
    answer: 'Answer',
    signatureName: 'Name',
    noFindings: 'No defects were found.',
    noDefects: 'No defects were found.',
    positives: 'Positive findings',
    positive: 'Positive',
    positiveCount_one: 'Plus {{count}} positive finding.',
    positiveCount_other: 'Plus {{count}} positive findings.',
    responsible: 'Responsible / deadline',
    responsibleValue: 'Responsible: {{name}}',
    dueBy: 'by {{date}}',
    dueNote: 'Deadline: {{text}}',
    number: 'No.',
    title: 'Title',
    category: 'Category',
    priority: 'Priority',
    status: 'Status',
    positions_one: 'Found at {{count}} location, see the defect plan: {{plans}}.',
    positions_other: 'Found at {{count}} locations; the defect plans show where: {{plans}}.',
    planUnavailable: 'The plan could not be loaded.',
    planDetail: 'Plan detail – {{plan}}',
    photoUnavailable: 'Photo not available',
    priorityValue: 'Priority: {{value}}',
    statusValue: 'Status: {{value}}',
    foundOn: 'Found: {{date}}',
    dueOn: 'Due: {{date}}',
    resolvedOn: 'Resolved: {{date}}',
    place: 'Location: {{place}}',
    measure: 'Recommended measure: {{text}}',
    legalBasisValue: 'Legal basis: {{text}}',
    legalBases: 'Legal bases:',
    recommendation: 'Recommendation: {{text}}',
    finding: 'Defect or finding',
    legalBasis: 'Legal basis / regulation',
    location: 'Location',
    photos: 'Photos',
    noPosition: 'No location marked',
    continuedRow: '{{title}} (continued)',
    noCategory: 'No category',
    noGroup: 'No group',
    planPage: 'Page {{page}}',
    planMarkers_one: '{{count}} marker',
    planMarkers_other: '{{count}} markers',
    planFindings_one: '{{count}} defect entered',
    planFindings_other: '{{count}} defects entered',
    planShared_one: 'several defects at {{count}} spot (numbers listed on the marker)',
    planShared_other: 'several defects at {{count}} spots (numbers listed on the marker)',
    geo: 'GPS {{point}}',
    geoAccuracy: 'GPS {{point}} (±{{meters}} m)',
    map: 'Defect map',
    mapOverview: 'Overview map',
    mapSection: 'Section {{index}} of {{count}}',
    mapSectionShort: 'Section {{index}}',
    mapSections_one: 'in {{count}} section on the next page',
    mapSections_other: 'in {{count}} sections on the following pages',
    mapFindings_one: '{{count}} defect with a GPS point',
    mapFindings_other: '{{count}} defects with a GPS point',
    mapSectionFindings_one: 'Defect {{labels}}',
    mapSectionFindings_other: 'Defects {{labels}}',
    mapDetail: 'Map detail',
    mapAttribution: 'Map: © OpenStreetMap contributors, OpenFreeMap',
    mapUnavailable:
      'The map could not be loaded, e.g. without a connection. The numbers stand where they are.',
  },
};
